/**
 * @file texts and css class names for post color values
 */
import { computeLuminanceRatio, RGB, rgba, RGBA, serializeRgbaStringToRgba } from 'bits/color'
import { __ } from 'bits/intl'
import type { PostColor } from 'types'

function postColorText({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return __('Red')
    case 'orange':
      return isLightColorScheme ? __('Yellow') : __('Orange')
    case 'green':
      return __('Green')
    case 'blue':
      return __('Blue')
    case 'purple':
      return __('Purple')
    default:
      return isLightColorScheme ? __('White') : __('Black')
  }
}

// dark/light post color depends on the wall's colorScheme setting, not user's browser color scheme
function oldPostColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? 'bg-old-post-red' : 'bg-old-post-red-dark'
    case 'orange':
      return isLightColorScheme ? 'bg-old-post-orange' : 'bg-old-post-orange-dark'
    case 'green':
      return isLightColorScheme ? 'bg-old-post-green' : 'bg-old-post-green-dark'
    case 'blue':
      return isLightColorScheme ? 'bg-old-post-blue' : 'bg-old-post-blue-dark'
    case 'purple':
      return isLightColorScheme ? 'bg-old-post-purple' : 'bg-old-post-purple-dark'
    default:
      return isLightColorScheme ? 'bg-old-post-light' : 'bg-old-post-dark'
  }
}

function postSelectorBackgroundColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? 'bg-scarlet-100' : 'bg-scarlet-800'
    case 'orange':
      return isLightColorScheme ? 'bg-canary-100' : 'bg-tangerine-800'
    case 'green':
      return isLightColorScheme ? 'bg-park-100' : 'bg-park-800'
    case 'blue':
      return isLightColorScheme ? 'bg-oceanic-100' : 'bg-oceanic-800'
    case 'purple':
      return isLightColorScheme ? 'bg-grape-100' : 'bg-grape-800'
    default:
      return isLightColorScheme ? 'bg-light-ui-200' : 'bg-dark-ui-100'
  }
}

function postSelectorHoverBackgroundColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? 'hover-hover:hover:bg-scarlet-200' : 'hover-hover:hover:bg-scarlet-700'
    case 'orange':
      return isLightColorScheme ? 'hover-hover:hover:bg-canary-200' : 'hover-hover:hover:bg-tangerine-700'
    case 'green':
      return isLightColorScheme ? 'hover-hover:hover:bg-park-200' : 'hover-hover:hover:bg-park-700'
    case 'blue':
      return isLightColorScheme ? 'hover-hover:hover:bg-oceanic-200' : 'hover-hover:hover:bg-oceanic-700'
    case 'purple':
      return isLightColorScheme ? 'hover-hover:hover:bg-grape-200' : 'hover-hover:hover:bg-grape-700'
    default:
      return isLightColorScheme ? 'hover-hover:hover:bg-light-ui-300' : 'hover-hover:hover:bg-dark-ui-300'
  }
}

// Use when hovering over the parent group should also change the background color, rather than only when hovering the color chip itself
// Note: Parent must have `group` class
function postSelectorGroupHoverBackgroundColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? 'hover-hover:group-hover:bg-scarlet-200' : 'hover-hover:group-hover:bg-scarlet-700'
    case 'orange':
      return isLightColorScheme ? 'hover-hover:group-hover:bg-canary-200' : 'hover-hover:group-hover:bg-tangerine-700'
    case 'green':
      return isLightColorScheme ? 'hover-hover:group-hover:bg-park-200' : 'hover-hover:group-hover:bg-park-700'
    case 'blue':
      return isLightColorScheme ? 'hover-hover:group-hover:bg-oceanic-200' : 'hover-hover:group-hover:bg-oceanic-700'
    case 'purple':
      return isLightColorScheme ? 'hover-hover:group-hover:bg-grape-200' : 'hover-hover:group-hover:bg-grape-700'
    default:
      return isLightColorScheme ? 'hover-hover:group-hover:bg-light-ui-300' : 'hover-hover:group-hover:bg-dark-ui-300'
  }
}

function postSelectorRingColorTailwindClasses({ isLightColorScheme }: { isLightColorScheme: boolean }): string[] {
  return ['ring-1', 'ring-inset', isLightColorScheme ? 'ring-dark-ui-stroke' : 'ring-light-ui-stroke']
}

function postColorClasses(color: PostColor, isLightColorScheme: boolean): string[] {
  return [
    postSelectorBackgroundColorTailwindClass({ postColor: color, isLightColorScheme }),
    postSelectorGroupHoverBackgroundColorTailwindClass({
      postColor: color,
      isLightColorScheme,
    }),
    ...postSelectorRingColorTailwindClasses({ isLightColorScheme }),
  ]
}

function postColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? 'bg-scarlet-100' : 'bg-scarlet-900'
    case 'orange':
      return isLightColorScheme ? 'bg-canary-100' : 'bg-tangerine-900'
    case 'green':
      return isLightColorScheme ? 'bg-park-100' : 'bg-park-900'
    case 'blue':
      return isLightColorScheme ? 'bg-oceanic-100' : 'bg-oceanic-900'
    case 'purple':
      return isLightColorScheme ? 'bg-grape-100' : 'bg-grape-900'
    default:
      return isLightColorScheme ? 'bg-light-ui-100' : 'bg-dark-ui-100'
  }
}

function postApprovalColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? 'bg-scarlet-200' : 'bg-scarlet-800'
    case 'orange':
      return isLightColorScheme ? 'bg-canary-200' : 'bg-tangerine-800'
    case 'green':
      return isLightColorScheme ? 'bg-park-200' : 'bg-park-800'
    case 'blue':
      return isLightColorScheme ? 'bg-oceanic-200' : 'bg-oceanic-800'
    case 'purple':
      return isLightColorScheme ? 'bg-grape-200' : 'bg-grape-800'
    default:
      return isLightColorScheme ? 'bg-light-ui-200' : 'bg-dark-ui-200'
  }
}

const postColorValues: PostColor[] = [null, 'red', 'orange', 'green', 'blue', 'purple']

const OLD_POST_RED = rgba(255, 205, 210, 1) // #FFCDD2
const OLD_POST_RED_DARK = rgba(183, 28, 28, 1) // #B71C1C
const OLD_POST_ORANGE = rgba(255, 236, 179, 1) // #FFECB3
const OLD_POST_ORANGE_DARK = rgba(255, 111, 0, 1) // #FF6F00
const OLD_POST_GREEN = rgba(200, 230, 201, 1) // #C8E6C9
const OLD_POST_GREEN_DARK = rgba(27, 94, 32, 1) // #1B5E20
const OLD_POST_BLUE = rgba(179, 229, 252, 1) // #B3E5FC
const OLD_POST_BLUE_DARK = rgba(13, 71, 161, 1) // #0D47A1
const OLD_POST_PURPLE = rgba(225, 190, 231, 1) // #E1BEE7
const OLD_POST_PURPLE_DARK = rgba(74, 20, 140, 1) // #4A148C
const OLD_POST_LIGHT = rgba(255, 255, 255, 1) // #FFFFFF
const OLD_POST_DARK = rgba(33, 33, 33, 1) // #212121

const POST_RED = rgba(255, 218, 218, 1.0) // #FFDADA
const POST_RED_DARK = rgba(124, 0, 0, 1.0) // #7C0000
const POST_ORANGE = rgba(255, 244, 206, 1.0) // #FFF4CE
const POST_ORANGE_DARK = rgba(145, 61, 0, 1.0) // #913D00
const POST_GREEN = rgba(222, 255, 221, 1.0) // #DEFFDD
const POST_GREEN_DARK = rgba(0, 61, 0, 1.0) // #003D00
const POST_BLUE = rgba(187, 234, 255, 1.0) // #BBEAFF
const POST_BLUE_DARK = rgba(1, 50, 107, 1.0) // #01326B
const POST_PURPLE = rgba(238, 216, 255, 1.0) // #EED8FF
const POST_PURPLE_DARK = rgba(60, 0, 98, 1.0) // #3D0062
const POST_LIGHT = rgba(255, 255, 255, 1.0) // #FFFFFF
const POST_DARK = rgba(17, 17, 17, 1.0) // #111111

const POST_APPROVAL_RED = rgba(255, 198, 203, 1.0) // #FFC6CB
const POST_APPROVAL_RED_DARK = rgba(152, 0, 0, 1.0) // #980000
const POST_APPROVAL_ORANGE = rgba(255, 235, 164, 1.0) // #FFEBA4
const POST_APPROVAL_ORANGE_DARK = rgba(187, 79, 0, 1.0) // #BB4F00
const POST_APPROVAL_GREEN = rgba(181, 255, 184, 1.0) // #B5FFB8
const POST_APPROVAL_GREEN_DARK = rgba(0, 78, 0, 1.0) // #004E00
const POST_APPROVAL_BLUE = rgba(154, 221, 251, 1.0) // #9ADDFB
const POST_APPROVAL_BLUE_DARK = rgba(0, 65, 142, 1.0) // #00418E
const POST_APPROVAL_PURPLE = rgba(231, 191, 255, 1.0) // #E7BFFF
const POST_APPROVAL_PURPLE_DARK = rgba(82, 0, 133, 1.0) // #520085
const POST_APPROVAL_LIGHT = rgba(245, 246, 247, 1.0) // #F5F6F7
const POST_APPROVAL_DARK = rgba(36, 37, 39, 1.0) // #242527

const DARK_UI_100 = rgba(17, 17, 17, 1) // #111111
const LIGHT_UI_100 = rgba(255, 255, 255, 1) //#FFFFFF
const DARK_UI_200 = rgba(33, 34, 36, 1) // #212224
const LIGHT_UI_200 = rgba(246, 246, 246, 1) //#F6F6F6

function oldPostColorRgb({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): RGB {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? OLD_POST_RED : OLD_POST_RED_DARK
    case 'orange':
      return isLightColorScheme ? OLD_POST_ORANGE : OLD_POST_ORANGE_DARK
    case 'green':
      return isLightColorScheme ? OLD_POST_GREEN : OLD_POST_GREEN_DARK
    case 'blue':
      return isLightColorScheme ? OLD_POST_BLUE : OLD_POST_BLUE_DARK
    case 'purple':
      return isLightColorScheme ? OLD_POST_PURPLE : OLD_POST_PURPLE_DARK
    default:
      return isLightColorScheme ? OLD_POST_LIGHT : OLD_POST_DARK
  }
}

function postColorRgb({ postColor, isLightColorScheme }: { postColor: PostColor; isLightColorScheme: boolean }): RGB {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? POST_RED : POST_RED_DARK
    case 'orange':
      return isLightColorScheme ? POST_ORANGE : POST_ORANGE_DARK
    case 'green':
      return isLightColorScheme ? POST_GREEN : POST_GREEN_DARK
    case 'blue':
      return isLightColorScheme ? POST_BLUE : POST_BLUE_DARK
    case 'purple':
      return isLightColorScheme ? POST_PURPLE : POST_PURPLE_DARK
    default:
      return isLightColorScheme ? POST_LIGHT : POST_DARK
  }
}

function postApprovalColorRgb({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): RGBA {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? POST_APPROVAL_RED : POST_APPROVAL_RED_DARK
    case 'orange':
      return isLightColorScheme ? POST_APPROVAL_ORANGE : POST_APPROVAL_ORANGE_DARK
    case 'green':
      return isLightColorScheme ? POST_APPROVAL_GREEN : POST_APPROVAL_GREEN_DARK
    case 'blue':
      return isLightColorScheme ? POST_APPROVAL_BLUE : POST_APPROVAL_BLUE_DARK
    case 'purple':
      return isLightColorScheme ? POST_APPROVAL_PURPLE : POST_APPROVAL_PURPLE_DARK
    default:
      return isLightColorScheme ? POST_APPROVAL_LIGHT : POST_APPROVAL_DARK
  }
}

export {
  computeLuminanceRatio,
  oldPostColorRgb,
  oldPostColorTailwindClass,
  postApprovalColorRgb,
  postApprovalColorTailwindClass,
  postColorRgb,
  postColorText,
  postSelectorRingColorTailwindClasses,
  postColorClasses,
  postSelectorBackgroundColorTailwindClass,
  postSelectorHoverBackgroundColorTailwindClass,
  postSelectorGroupHoverBackgroundColorTailwindClass,
  postColorTailwindClass,
  postColorValues,
  serializeRgbaStringToRgba,
  // Constants
  DARK_UI_100,
  LIGHT_UI_100,
  DARK_UI_200,
  LIGHT_UI_200,
}
export type { RGB, RGBA }
